import React, { useEffect, useRef, useState } from "react";

type TVideoElement = React.VideoHTMLAttributes<HTMLVideoElement> & {
  fixedHeightClass?: string;
  stretchPosterImage?: boolean;
  useVideoARForDimension?: // use video aspect ratio for dimensions
  | {
        video_height: number;
        video_width: number;
      }
    | undefined;
  wrapperClassName?: string;
  videoAddress?:string;
  wrapperStyle?: React.CSSProperties;
};

const AppVideoElement = React.forwardRef<HTMLVideoElement, TVideoElement>(
  (
    {
      className = "",
      controls,
      fixedHeightClass = "h-96",
      muted,
      playsInline,
      poster,
      src,
      videoAddress,
      stretchPosterImage = false,
      useVideoARForDimension,
      wrapperClassName = "",
      wrapperStyle = {},
      ...moreHTMLVideoElementProps
    },
    ref
  ) => {
    const videoDimensionsAvailable =
      !!useVideoARForDimension &&
      !!useVideoARForDimension.video_width &&
      !!useVideoARForDimension.video_height;
    const heightPerc = !!useVideoARForDimension
      ? (
          (useVideoARForDimension.video_height * 100) /
          useVideoARForDimension.video_width
        ).toFixed(2)
      : 25;

    const videoRef = useRef<HTMLVideoElement>(null);
    const [videoClicked, setVideoClicked] = useState(false);

    const onVideoBeingPlayed = () => {
      setVideoClicked(true);
    };

    useEffect(() => {
      (ref as React.RefObject<HTMLVideoElement>)?.current?.addEventListener(
        "play",
        onVideoBeingPlayed
      );
      return () => {
        (
          ref as React.RefObject<HTMLVideoElement>
        )?.current?.removeEventListener("play", onVideoBeingPlayed);
      };
    }, []);

    return (
      <div
        className={
          `video-wrapper relative w-full bg-black rounded-lg overflow-hidden ` +
          wrapperClassName
        }
        style={{
          ...(useVideoARForDimension ? { paddingTop: `${heightPerc}%` } : {}),
          ...wrapperStyle,
        }}
      >
        {!!videoAddress && (
        <div className="absolute top-0 left-0 z-30 p-4 text-white font-medium pointer-events-none">
        {videoAddress}
        </div>
        )}
        {/* poster image generated by VisionMax combined (side-by-side, grid, etc.) video recordings does not respect aspect ratio of the video recording, thus, */}
        {/* the following approach to display video poster image ensures correct alignment of poster with video and also ensures correct aspect ratio of video playback in fulscreen mode */}
        {!!stretchPosterImage && !videoClicked && (
          <div
            className="group absolute top-0 left-0 w-full h-full rounded-lg flex items-center justify-center z-20 cursor-pointer after:absolute after:w-full after:h-full after:top-0 after:left-0 after:bg-black/40 hover:after:bg-black/50 after:z-20 after:transition-all"
            onClick={() => {
              setVideoClicked(true);
              (videoRef as any)?.current?.play();
              (ref as any)?.current?.play();
            }}
          >
            <img
              alt=""
              className={`absolute object-fill top-0 left-0 w-full h-full rounded-lg z-10`}
              src={poster}
            />
            <span className="relative translate-y-6 font-bold text-white z-30 before:content-[''] before:block before:absolute before:left-1/2 before:-translate-x-1/2 before:-translate-y-8 before:border-transparent before:transition-all before:border-l-white/70 group-hover:before:border-l-white before:border-l-[20px] before:border-t-[12px] before:border-b-[12px] before:border-r-0">
              Click to play
            </span>
          </div>
        )}
        <video
          ref={ref || videoRef}
          controls={controls}
          disablePictureInPicture={true}
          muted={muted}
          playsInline={playsInline}
          className={
            `bg-black rounded-lg w-full ` +
            `${
              videoDimensionsAvailable
                ? `dim-available absolute top-0 left-0 h-full shadow-lg`
                : fixedHeightClass
            } ` +
            className +
            ` z-10`
          }
          poster={!!stretchPosterImage ? undefined : poster}
          src={src}
          {...moreHTMLVideoElementProps}
        />
      </div>
    );
  }
);
export default AppVideoElement;
